<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/slider';
import {HeaderInfo, FunctionAPI, TypeContext, InterfaceType, TypeLink} from '@react-spectrum/docs';
import packageData from '@react-aria/slider/package.json';
import statelyDocs from 'docs:@react-stately/slider';
import i18nDocs from 'docs:@react-aria/i18n';

```jsx import
import {useSlider, useSliderThumb} from '@react-aria/slider';
```

---
category: Forms
keywords: [slider, range, aria]
---

# useSlider

<p>{docs.exports.useSlider.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useSlider', 'useSliderThumb']}
  sourceData={[
    {type: 'W3C', url: 'https://www.w3.org/TR/wai-aria-1.2/#slider'}
  ]} />

## API

<FunctionAPI function={docs.exports.useSlider} links={docs.links} />
<FunctionAPI function={docs.exports.useSliderThumb} links={docs.links} />

## Features

The [&lt;input type="range"&gt;](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range)
HTML element can be used to build a slider, however it is
very difficult to style cross browser. `useSlider` and `useSliderThumb` help achieve accessible
sliders that can be styled as needed.

* Support for mouse, touch, and keyboard via the [useMove](../react-aria/useMove.html) hook
* Exposed to assistive technology as a `group` of `slider` elements via ARIA
* Slider thumbs use hidden native input elements to support touch screen readers
* Labeling support for accessibility
* Internationalized number formatting as a percentage or value
* Support for multiple thumbs, e.g. a group of range values
* Support for both horizontal and vertical orientations
* Prevents text selection while dragging

## Anatomy

Sliders consist of a track element showing the range of available values,
one or more thumb thumbs showing the current values, an
optional [&lt;output&gt;](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/output)
element displaying the current values textually, and a label.
The thumbs can be dragged to allow a user to change their value. In addition, the
track can be clicked to move the nearest thumb to that position.

### useSlider hook
`useSlider` returns three sets of props that you should spread onto the appropriate element:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useSlider.return.id].properties} />
</TypeContext.Provider>

If there is no visual label, an `aria-label` or `aria-labelledby` prop must be passed instead
to identify the element to screen readers.

### useSliderThumb hook

`useSliderThumb` returns three sets of props that you should spread onto the appropriate element:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useSliderThumb.return.id].properties} />
</TypeContext.Provider>

If there is no visual label, an `aria-label` or `aria-labelledby` prop must be passed instead
to identify each thumb to screen readers.

Slider state is managed by the <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useSliderState} /> hook.

## Examples

### Single Thumb

```tsx example
import {useSliderState} from '@react-stately/slider';
import {useFocusRing} from '@react-aria/focus';
import {VisuallyHidden} from '@react-aria/visually-hidden';
import {mergeProps} from '@react-aria/utils';

function Slider(props) {
  let trackRef = React.useRef(null);

  let state = useSliderState(props);
  let {
    containerProps,
    trackProps,
    labelProps,
    outputProps
  } = useSlider(props, state, trackRef);

  return (
    <div
      {...containerProps}
      style={{position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 300, touchAction: 'none'}}>
      <div style={{display: 'flex', alignSelf: 'stretch'}}>
        {props.label &&
          <label {...labelProps}>{props.label}</label>
        }
        <output {...outputProps} style={{flex: '1 0 auto', textAlign: 'end'}}>
          {state.getThumbValueLabel(0)}
        </output>
      </div>
      <div style={{position: 'relative', width: '100%'}}>
        <div style={{
          position: 'absolute',
          backgroundColor: 'gray',
          height: 3,
          top: 13,
          width: '100%'
        }} />
        <div
          {...trackProps}
          ref={trackRef}
          style={{
            position: 'relative',
            height: 30,
            width:' 100%'
          }} />
        <Thumb index={0} state={state} trackRef={trackRef} />
      </div>
    </div>
  );
}

function Thumb(props) {
  let {state, trackRef, index} = props;
  let inputRef = React.useRef(null);
  let {thumbProps, inputProps} = useSliderThumb({
    index,
    trackRef,
    inputRef
  }, state);

  let {focusProps, isFocusVisible} = useFocusRing();
  return (
    <div
      style={{
        position: 'absolute',
        top: 4,
        transform: 'translateX(-50%)',
        left: `${state.getThumbPercent(index) * 100}%`
      }}>
      <div
        {...thumbProps}
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: isFocusVisible
            ? 'orange'
            : state.isThumbDragging(index)
              ? 'dimgrey'
              : 'gray'
        }}>
        <VisuallyHidden>
          <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
        </VisuallyHidden>
      </div>
    </div>
  );
}

<Slider
  label="Opacity"
  formatOptions={{style: 'percent'}}
  maxValue={1}
  step={0.01} />
```

### Multi Thumb
```tsx example
function RangeSlider(props) {
  let trackRef = React.useRef(null);

  let state = useSliderState(props);
  let {
    containerProps,
    trackProps,
    labelProps,
    outputProps
  } = useSlider(props, state, trackRef);

  return (
    <div
      {...containerProps}
      style={{position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 300, touchAction: 'none'}}>
      <div style={{display: 'flex', alignSelf: 'stretch'}}>
        {props.label &&
          <label {...labelProps}>{props.label}</label>
        }
        <output {...outputProps} style={{flex: '1 0 auto', textAlign: 'end'}}>
          {`${state.getThumbValueLabel(0)} - ${state.getThumbValueLabel(1)}`}
        </output>
      </div>
      <div style={{position: 'relative', width: '100%'}}>
        <div style={{
          position: 'absolute',
          backgroundColor: 'grey',
          height: 3,
          top: 13,
          width: '100%'
        }} />
        <div
          {...trackProps}
          ref={trackRef}
          style={{
            position: 'relative',
            height: 30,
            width:' 100%'
          }} />
        <Thumb index={0} state={state} trackRef={trackRef} />
        <Thumb index={1} state={state} trackRef={trackRef} />
      </div>
    </div>
  );
}

///- begin collapse -///
function Thumb(props) {
  let {state, trackRef, index} = props;
  let inputRef = React.useRef(null);
  let {thumbProps, inputProps} = useSliderThumb({
    index,
    trackRef,
    inputRef
  }, state);

  let {focusProps, isFocusVisible} = useFocusRing();
  return (
    <div
      style={{
        position: 'absolute',
        top: 4,
        transform: 'translateX(-50%)',
        left: `${state.getThumbPercent(index) * 100}%`
      }}>
      <div
        {...thumbProps}
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: isFocusVisible
            ? 'orange'
            : state.isThumbDragging(index)
              ? 'dimgrey'
              : 'gray'
        }}>
        <VisuallyHidden>
          <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
        </VisuallyHidden>
      </div>
    </div>
  );
}
///- end collapse -///

<RangeSlider
  label="Price Range"
  formatOptions={{style: 'currency', currency: 'USD'}}
  maxValue={500}
  defaultValue={[100, 350]}
  step={10} />
```

## Internationalization

### Value formatting

Formatting the value that should be displayed in the value label or `aria-valuetext`
is handled by <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useSliderState} />.
The formatting can be controlled using the `formatOptions` prop.
If you want to change locales, the <TypeLink links={i18nDocs.links} type={i18nDocs.exports.I18nProvider} />
must be somewhere in the hierarchy above the Slider.
This will tell the formatter what locale to use.

### RTL

In right-to-left languages, the slider should be mirrored. The label is right-aligned,
the value is left-aligned. Ensure that your CSS accounts for this.
